import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Search, Trash2, Download, Film, Image as ImageIcon, Library, Cloud } from "lucide-react";
import { api, mediaUrl } from "../../lib/api";
import SelectField from "../../components/os/SelectField";

const isVideo = (u = "") => /\.(mp4|webm|mov)(\?|$)/i.test(u);

function AssetCard({ src, video, title, sub, actions }) {
  return (
    <div className="relative group rounded-xl overflow-hidden border border-navy/10 bg-navy/[0.02]">
      {video ? <video src={src} className="w-full h-36 object-cover" muted /> : <img src={src} alt={title} className="w-full h-36 object-cover" />}
      <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-navy/70 to-transparent">
        <div className="text-white text-xs font-medium truncate">{title}</div>
        {sub && <div className="text-white/60 text-[10px] truncate">{sub}</div>}
      </div>
      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">{actions}</div>
    </div>
  );
}

export default function ModuleAssetBank({ moduleKey, items, onOpen, onRefresh }) {
  const [tab, setTab] = useState("library");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const [q, setQ] = useState("");
  const [source, setSource] = useState("unsplash");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const vid = /\.(mp4|webm|mov)$/i.test(file.name);
      const rec = (await api.post(`/mod/${moduleKey}/items`, { nombre: file.name, tipo: vid ? "Video" : "Imagen", categoria: "Subido" })).data;
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/mod/${moduleKey}/items/${rec.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Activo subido");
      onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al subir"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true); setResults([]);
    try {
      const { data } = await api.get(`/mod/${moduleKey}/stock`, { params: { q, source } });
      setResults(data.results || []);
      if (!data.results?.length) toast.info("Sin resultados");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error en la búsqueda"); }
    finally { setSearching(false); }
  };

  const importAsset = async (r) => {
    try {
      await api.post(`/mod/${moduleKey}/import`, { nombre: `${q} — ${r.author || source}`, tipo: r.type === "video" ? "Video" : "Imagen", categoria: "Banco de imágenes", etiquetas: q, url: r.full, fuente: source });
      toast.success("Guardado en tu biblioteca");
      onRefresh?.();
    } catch (e) { toast.error("No se pudo guardar"); }
  };

  const del = async (id) => { try { await api.delete(`/mod/${moduleKey}/items/${id}`); onRefresh?.(); } catch {} };
  const previewOf = (it) => it.preview_url || (it.files?.[0]?.url ? mediaUrl(it.files[0].url) : "");

  const Tab = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTab(id)} data-testid={`assetbank-tab-${id}`}
      className={`text-sm px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors ${tab === id ? "bg-gradient-violet text-white" : "bg-navy/[0.04] text-navy/60"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="module-asset-bank">
      <div className="flex gap-2 flex-wrap">
        <Tab id="library" icon={Library} label="Mi biblioteca" />
        <Tab id="upload" icon={Upload} label="Subir" />
        <Tab id="stock" icon={Cloud} label="Bancos (Unsplash / Pexels)" />
      </div>

      {tab === "library" && (
        items.length === 0
          ? <div className="card-surface py-14 text-center text-navy/40 text-sm">Tu biblioteca está vacía. Sube archivos o busca en los bancos.</div>
          : <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="assetbank-library">
            {items.map((it) => {
              const src = previewOf(it);
              return (
                <div key={it.id} onClick={() => onOpen?.(it)} className="cursor-pointer" data-testid={`asset-${it.id}`}>
                  {src ? <AssetCard src={src} video={isVideo(src)} title={it.nombre} sub={it.categoria}
                    actions={<button onClick={(e) => { e.stopPropagation(); del(it.id); }} className="w-6 h-6 rounded bg-white/90 flex items-center justify-center text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>} />
                    : <div className="rounded-xl border border-navy/10 h-36 flex flex-col items-center justify-center text-navy/30 gap-1"><ImageIcon className="w-6 h-6" /><span className="text-xs px-2 text-center truncate w-full">{it.nombre}</span></div>}
                </div>
              );
            })}
          </div>
      )}

      {tab === "upload" && (
        <div className="card-surface p-8 text-center space-y-3">
          <input ref={fileRef} type="file" className="hidden" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={upload} data-testid="assetbank-file-input" />
          <ImageIcon className="w-10 h-10 text-navy/20 mx-auto" />
          <p className="text-sm text-navy/50">Sube imágenes (PNG, JPG, WEBP) o videos (MP4, WEBM, MOV) hasta 200 MB.</p>
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn btn-primary mx-auto" data-testid="assetbank-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Seleccionar archivo
          </button>
        </div>
      )}

      {tab === "stock" && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <div className="w-48"><SelectField testid="assetbank-source" value={source} options={[{ value: "unsplash", label: "Unsplash (imágenes)" }, { value: "pexels", label: "Pexels (imágenes)" }, { value: "pexels_video", label: "Pexels (video)" }]} onChange={setSource} /></div>
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Buscar activos… (ej. evento corporativo)" data-testid="assetbank-search-input"
              className="flex-1 min-w-[200px] bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm" />
            <button onClick={search} disabled={searching} className="btn btn-primary" data-testid="assetbank-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Buscar
            </button>
          </div>
          {results.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="assetbank-results">
              {results.map((r) => (
                <AssetCard key={r.id} src={r.thumb} video={r.type === "video"} title={r.author || source} sub={r.type === "video" ? "Video" : "Imagen"}
                  actions={<>
                    <a href={r.full} target="_blank" rel="noreferrer" className="w-6 h-6 rounded bg-white/90 flex items-center justify-center text-navy/70"><Download className="w-3.5 h-3.5" /></a>
                    <button onClick={() => importAsset(r)} className="px-2 h-6 rounded bg-violet text-white text-[10px] font-medium" data-testid={`assetbank-import-${r.id}`}>Guardar</button>
                  </>} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
